import { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { RotateCcw, RotateCw, ZoomIn } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Slider } from "../../components/ui/slider";
import { useLang } from "../../lib/i18n";

const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.onload = () => resolve(img);
  img.onerror = reject;
  img.src = src;
});

// Rotate the full image, then cut out the crop area (react-easy-crop gives pixels in the rotated frame).
const renderCrop = async (src, area, rotation) => {
  const img = await loadImage(src);
  const rad = (rotation * Math.PI) / 180;
  const bw = Math.abs(Math.cos(rad) * img.width) + Math.abs(Math.sin(rad) * img.height);
  const bh = Math.abs(Math.sin(rad) * img.width) + Math.abs(Math.cos(rad) * img.height);
  const rotated = document.createElement("canvas");
  rotated.width = bw; rotated.height = bh;
  const rctx = rotated.getContext("2d");
  rctx.translate(bw / 2, bh / 2);
  rctx.rotate(rad);
  rctx.drawImage(img, -img.width / 2, -img.height / 2);
  const out = document.createElement("canvas");
  const size = Math.min(1024, Math.round(area.width));
  out.width = size; out.height = size;
  out.getContext("2d").drawImage(rotated, area.x, area.y, area.width, area.height, 0, 0, size, size);
  return new Promise((resolve) => out.toBlob(resolve, "image/jpeg", 0.9));
};

export const PhotoEditor = ({ src, onCancel, onSave, busy }) => {
  const { t } = useLang();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [area, setArea] = useState(null);
  const onComplete = useCallback((_, px) => setArea(px), []);
  const save = async () => onSave(await renderCrop(src, area, rotation));
  const rotate = (d) => setRotation((r) => (r + d + 360) % 360);

  return (
    <Dialog open={!!src} onOpenChange={(o) => !o && !busy && onCancel()}>
      <DialogContent className="max-w-md rounded-3xl" data-testid="photo-editor" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display">{t("Edit photo")}</DialogTitle></DialogHeader>
        <div className="relative h-72 overflow-hidden rounded-2xl bg-slate-900">
          {src && <Cropper image={src} crop={crop} zoom={zoom} rotation={rotation} aspect={1} cropShape="round" showGrid={false}
            onCropChange={setCrop} onZoomChange={setZoom} onRotationChange={setRotation} onCropComplete={onComplete} />}
        </div>
        <div className="flex items-center gap-3" dir="ltr">
          <ZoomIn className="h-4 w-4 shrink-0 text-slate-500" />
          <Slider value={[zoom]} min={1} max={3} step={0.01} onValueChange={([v]) => setZoom(v)} data-testid="photo-zoom-slider" />
        </div>
        <div className="flex items-center gap-2" dir="ltr">
          <button type="button" onClick={() => rotate(-90)} title={t("Rotate left")} data-testid="photo-rotate-left"
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-300 hover:border-slate-900"><RotateCcw className="h-4 w-4" /></button>
          <Slider value={[rotation]} min={0} max={359} step={1} onValueChange={([v]) => setRotation(v)} data-testid="photo-rotation-slider" />
          <button type="button" onClick={() => rotate(90)} title={t("Rotate right")} data-testid="photo-rotate-right"
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-300 hover:border-slate-900"><RotateCw className="h-4 w-4" /></button>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={onCancel} disabled={busy} data-testid="photo-editor-cancel"
            className="btn-press min-h-[48px] flex-1 rounded-2xl border border-slate-300 font-semibold text-slate-700">{t("Cancel")}</button>
          <button type="button" onClick={save} disabled={busy || !area} data-testid="photo-editor-save"
            className="btn-press min-h-[48px] flex-1 rounded-2xl bg-slate-900 font-semibold text-white disabled:opacity-60">{busy ? t("Saving…") : t("Save photo")}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
